import React, { useState, useEffect } from 'react';
import {
  Search,
  FlaskConical,
  Package,
  Bot,
  AlertCircle,
  CheckCircle,
  Loader,
  ChevronDown,
  ChevronUp,
  Eye,
  Save,
  RefreshCw,
  Info,
  X
} from 'lucide-react';
import axios from 'axios';
import { useClient } from '../../contexts/ClientContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

interface Product {
  id_catalogo: number;
  title: string;
  Marca: string;
  Cat: string;
  sitio: string;
  imagen: string;
  original_price: string;
  sale_price: string;
  url: string;
  catalog_brand?: string;
  catalog_category?: string;
  confidence_score?: number;
  cataloged_at?: string;
}

interface AnalysisResult {
  product_data: Product;
  ai_analysis: {
    catalog_brand: string | null;
    catalog_category: string | null;
    attributes: Record<string, any>;
    confidence_score: number;
    reasoning: string;
    image_insights: string;
  };
  rules_applied: {
    client_prompt: string;
    brand_rules: string;
    category_rules: string;
    total_categories: number;
    total_brands: number;
  };
  raw_response?: string;
}

interface CatalogTestingProps {
  selectedTable?: string;
}

const CatalogTesting: React.FC<CatalogTestingProps> = ({ selectedTable }) => {
  const { selectedClient } = useClient();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDebug, setShowDebug] = useState(false);
  const [onlyUncataloged, setOnlyUncataloged] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [, setTotalProducts] = useState(0);
  const [currentTable, setCurrentTable] = useState<string>('');
  const [clientAiService, setClientAiService] = useState<'openai' | 'gemini'>('openai');

  const ITEMS_PER_PAGE = 20;

  // Use passed selectedTable prop or get from localStorage as fallback
  useEffect(() => {
    if (selectedTable) {
      setCurrentTable(selectedTable);
    } else {
      // Fallback to localStorage if prop is not provided
      const savedTable = localStorage.getItem('selectedTable');
      if (savedTable) {
        setCurrentTable(savedTable);
      }
    }
  }, [selectedTable]);

  // Load client's AI service preference
  useEffect(() => {
    const loadClientAiService = async () => {
      if (selectedClient?.clientId) {
        try {
          const docRef = doc(db, 'clients', selectedClient.clientId);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data.aiService) {
              setClientAiService(data.aiService);
            }
          }
        } catch (error) {
          console.error('Error loading client AI service preference:', error);
        }
      }
    };
    
    loadClientAiService();
  }, [selectedClient]);

  // Load products when table or search changes
  useEffect(() => {
    if (currentTable && selectedClient) {
      searchProducts(1);
    }
  }, [currentTable, searchTerm, onlyUncataloged, selectedClient]);

  const searchProducts = async (page: number = 1) => {
    if (!currentTable || !selectedClient) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const headers = {
        'X-Client-Id': selectedClient.clientId,
        'X-Client-Name': selectedClient.clientName
      };
      
      const offset = (page - 1) * ITEMS_PER_PAGE;
      
      
      const response = await axios.get('/api/catalog/search-products', {
        headers,
        params: {
          table: currentTable,
          search: searchTerm || '',
          limit: ITEMS_PER_PAGE,
          offset,
          onlyUncataloged
        }
      });
      
      setProducts(response.data.products);
      setTotalProducts(response.data.total);
      setTotalPages(response.data.totalPages);
      setCurrentPage(page);
    } catch (err: any) {
      console.error('Error searching products:', err);
      setError('Error al buscar productos');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const analyzeProduct = async () => {
    if (!selectedProduct || !selectedClient) return;
    
    setAnalyzing(true);
    setError(null);
    setAnalysisResult(null);
    
    try {
      const headers = {
        'X-Client-Id': selectedClient.clientId,
        'X-Client-Name': selectedClient.clientName
      };
      
      const response = await axios.post('/api/catalog/analyze', {
        cliente_id: selectedClient.clientId,
        id_catalogo: selectedProduct.id_catalogo,
        table_name: currentTable,
        aiService: clientAiService // Use client's configured AI service
      }, { headers });
      
      setAnalysisResult(response.data);
    } catch (err: any) {
      console.error('Error analyzing product:', err);
      setError(err.response?.data?.error || 'Error al analizar el producto');
    } finally {
      setAnalyzing(false);
    }
  };

  const saveAnalysis = async () => {
    if (!analysisResult || !selectedProduct || !selectedClient) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const headers = {
        'X-Client-Id': selectedClient.clientId,
        'X-Client-Name': selectedClient.clientName
      };
      
      await axios.post('/api/catalog/save', {
        id_catalogo: selectedProduct.id_catalogo,
        cliente_id: selectedClient.clientId,
        catalog_brand: analysisResult.ai_analysis.catalog_brand,
        catalog_category: analysisResult.ai_analysis.catalog_category,
        attributes: analysisResult.ai_analysis.attributes,
        toma_table: currentTable,
        original_brand: selectedProduct.Marca,
        original_category: selectedProduct.Cat,
        confidence_score: analysisResult.ai_analysis.confidence_score,
        ai_reasoning: analysisResult.ai_analysis.reasoning,
        image_analysis: { insights: analysisResult.ai_analysis.image_insights }
      }, { headers });
      
      // Refresh the product list
      searchProducts(currentPage);
      
      // Clear selection
      setSelectedProduct(null);
      setAnalysisResult(null);
      
      alert('Catalogado guardado exitosamente');
    } catch (err: any) {
      console.error('Error saving catalog:', err);
      setError('Error al guardar el catalogado');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: string | number | null): string => {
    if (!price) return '-';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '-';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numPrice);
  };

  if (!selectedClient) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-yellow-600" />
          <div>
            <h3 className="font-semibold text-yellow-900">Cliente no seleccionado</h3>
            <p className="text-yellow-700 mt-1">
              Por favor, selecciona un cliente para usar el testeo de catalogado.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentTable) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-blue-600" />
          <div>
            <h3 className="font-semibold text-blue-900">Tabla no seleccionada</h3>
            <p className="text-blue-700 mt-1">
              Por favor, selecciona una tabla desde el Dashboard principal para comenzar el testeo de catalogado.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <FlaskConical className="w-8 h-8 text-purple-600" />
          <div>
            <h2 className="text-2xl font-bold">Testeo de Catalogado con IA</h2>
            <p className="text-gray-600">
              Analiza productos con inteligencia artificial para catalogarlos automáticamente
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Left Panel - Search & Products */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Search Controls */}
          <div className="p-5 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-gray-200 space-y-3">

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar en títulos de productos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 shadow-sm"
              />
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={onlyUncataloged}
                  onChange={(e) => setOnlyUncataloged(e.target.checked)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700">Solo sin catalogar</span>
              </label>
            </div>
          </div>

          {/* Products List */}
          <div className="h-[650px] overflow-y-auto bg-gray-50">
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <Loader className="w-6 h-6 animate-spin text-purple-600" />
              </div>
            ) : products.length === 0 ? (
              <div className="text-center p-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                No se encontraron productos
              </div>
            ) : (
              <div className="divide-y">
                {products.map(product => (
                  <div
                    key={product.id_catalogo}
                    onClick={() => setSelectedProduct(product)}
                    className={`p-4 hover:bg-white cursor-pointer transition-all duration-200 border-l-4 ${
                      selectedProduct?.id_catalogo === product.id_catalogo 
                        ? 'bg-gradient-to-r from-purple-50 to-transparent border-purple-500 shadow-sm' 
                        : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {product.imagen && (
                        <img
                          src={product.imagen}
                          alt=""
                          className="w-12 h-12 object-contain bg-gray-100 rounded"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-gray-700">ID: {product.id_catalogo}</span>
                          {product.catalog_brand && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium" title="Catalogado">
                              <CheckCircle className="w-3 h-3" />
                              Catalogado
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-900 line-clamp-2">{product.title}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Marca: {product.Marca || '-'} | Cat: {product.Cat || '-'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 bg-white border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-600 font-medium">
                Página {currentPage} de {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => searchProducts(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Anterior
                </button>
                <button
                  onClick={() => searchProducts(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Middle Panel - Product Details */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="p-6">
          {selectedProduct ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                <Package className="w-6 h-6 text-purple-600" />
                <h3 className="font-semibold text-lg text-gray-800">Detalles del Producto</h3>
              </div>
              
              {selectedProduct.imagen && (
                <img
                  src={selectedProduct.imagen}
                  alt={selectedProduct.title}
                  className="w-full h-64 object-contain bg-gray-50 rounded-lg"
                />
              )}
              
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-500">ID:</span>
                    <p className="mt-1">{selectedProduct.id_catalogo}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Sitio:</span>
                    <p className="mt-1">{selectedProduct.sitio}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Marca actual:</span>
                    <p className="mt-1">{selectedProduct.Marca || '-'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Categoría actual:</span>
                    <p className="mt-1">{selectedProduct.Cat || '-'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Precio:</span>
                    <p className="mt-1">{formatPrice(selectedProduct.sale_price)}</p>
                  </div>
                </div>
                
                <div>
                  <span className="font-medium text-gray-500">Título:</span>
                  <p className="mt-1 text-sm">{selectedProduct.title}</p>
                </div>
                
                {selectedProduct.url && (
                  <a
                    href={selectedProduct.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-600 hover:underline text-sm"
                  >
                    <Eye className="w-4 h-4" />
                    Ver producto original
                  </a>
                )}
              </div>

              {selectedProduct.catalog_brand && (
                <div className="p-3 bg-green-50 rounded-lg">
                  <div className="text-green-800 font-medium mb-1">Catalogado anteriormente:</div>
                  <div className="text-sm text-green-700">
                    Marca: {selectedProduct.catalog_brand}<br />
                    Categoría: {selectedProduct.catalog_category}<br />
                    Confianza: {((selectedProduct.confidence_score || 0) * 100).toFixed(0)}%
                  </div>
                </div>
              )}

              <button
                onClick={analyzeProduct}
                disabled={analyzing || !selectedProduct.imagen}
                className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
              >
                {analyzing ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Analizando...
                  </>
                ) : (
                  <>
                    <Bot className="w-5 h-5" />
                    Analizar con IA
                  </>
                )}
              </button>
              
              {!selectedProduct.imagen && (
                <p className="text-sm text-amber-600 text-center">
                  Este producto no tiene imagen para analizar
                </p>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <Package className="w-16 h-16 mb-4" />
              <p className="text-lg">Selecciona un producto</p>
              <p className="text-sm mt-2">para ver sus detalles</p>
            </div>
          )}
          </div>
        </div>

        {/* Right Panel - Analysis Results */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="p-6">
          {analysisResult ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800">
                  <Bot className="w-6 h-6 text-green-500" />
                  Resultado del Análisis IA
                </h3>
                <button
                  onClick={() => setAnalysisResult(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Main Results */}
              <div className="space-y-3">
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                  <h4 className="font-medium text-purple-900 mb-2">Marca Detectada</h4>
                  <div className="text-lg font-semibold text-purple-700">
                    {analysisResult.ai_analysis.catalog_brand || 'No determinada'}
                  </div>
                  <div className="text-sm text-purple-600 mt-1">
                    Confianza: {(analysisResult.ai_analysis.confidence_score * 100).toFixed(0)}%
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                  <h4 className="font-medium text-blue-900 mb-2">Categoría Asignada</h4>
                  <div className="text-lg font-semibold text-blue-700">
                    {analysisResult.ai_analysis.catalog_category || 'No determinada'}
                  </div>
                </div>
              </div>

              {/* Attributes */}
              {Object.keys(analysisResult.ai_analysis.attributes).length > 0 && (
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                  <h4 className="font-medium mb-3">Atributos Detectados</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(analysisResult.ai_analysis.attributes).map(([key, value]) => {
                      // Format the key for display - properly handle accented characters
                      const displayKey = key
                        .replace(/_/g, ' ')
                        .split(' ')
                        .map(word => {
                          // Only capitalize first letter, preserve the rest including accents
                          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                        })
                        .join(' ');
                      
                      // Format the value based on type
                      let displayValue = String(value);
                      let valueClass = "font-medium text-gray-900";
                      
                      if (value === null || value === undefined || value === '' || value === 'Sin descripción') {
                        displayValue = 'No detectado';
                        valueClass = "text-gray-400 italic";
                      } else if (value === true) {
                        displayValue = 'Sí';
                        valueClass = "font-medium text-green-600";
                      } else if (value === false) {
                        displayValue = 'No';
                        valueClass = "font-medium text-gray-600";
                      } else if (key === 'color') {
                        valueClass = "font-semibold text-gray-900";
                        displayValue = value.charAt(0).toUpperCase() + value.slice(1);
                      }
                      
                      return (
                        <div key={key} className="bg-white rounded-lg px-3 py-2 border border-gray-100">
                          <div className="text-xs text-gray-500">{displayKey}</div>
                          <div className={`text-sm mt-0.5 ${valueClass}`}>{displayValue}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Reasoning */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4 text-gray-500" />
                  Razonamiento
                </h4>
                <p className="text-sm text-gray-700">{analysisResult.ai_analysis.reasoning}</p>
              </div>

              {/* Image Insights */}
              {analysisResult.ai_analysis.image_insights && (
                <div className="bg-yellow-50 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-900 mb-2">Análisis de Imagen</h4>
                  <p className="text-sm text-yellow-800">{analysisResult.ai_analysis.image_insights}</p>
                </div>
              )}

              {/* Comparison */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3">Comparación</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500">
                      <th className="pb-2">Campo</th>
                      <th className="pb-2">Original</th>
                      <th className="pb-2">Catalogado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr>
                      <td className="py-2">Marca</td>
                      <td className="py-2">{selectedProduct?.Marca || '-'}</td>
                      <td className="py-2 font-medium">
                        {analysisResult.ai_analysis.catalog_brand || '-'}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2">Categoría</td>
                      <td className="py-2">{selectedProduct?.Cat || '-'}</td>
                      <td className="py-2 font-medium">
                        {analysisResult.ai_analysis.catalog_category || '-'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Debug Section */}
              <div className="border rounded-lg">
                <button
                  onClick={() => setShowDebug(!showDebug)}
                  className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm font-medium">Debug Info</span>
                  {showDebug ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showDebug && (
                  <div className="p-3 border-t">
                    <div className="text-xs text-gray-600 space-y-2">
                      <div>
                        <span className="font-medium">Reglas aplicadas:</span>
                        <ul className="mt-1 space-y-1">
                          <li>• Cliente: {analysisResult.rules_applied.client_prompt}</li>
                          <li>• Marcas: {analysisResult.rules_applied.brand_rules}</li>
                          <li>• Categorías: {analysisResult.rules_applied.category_rules}</li>
                        </ul>
                      </div>
                      <div>
                        <span className="font-medium">Totales:</span>
                        <ul className="mt-1 space-y-1">
                          <li>• Categorías: {analysisResult.rules_applied.total_categories}</li>
                          <li>• Marcas: {analysisResult.rules_applied.total_brands}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={saveAnalysis}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Guardar
                </button>
                <button
                  onClick={analyzeProduct}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Reanalizar
                </button>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-red-800">Error</div>
                  <div className="text-sm text-red-700 mt-1">{error}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <Bot className="w-16 h-16 mb-4" />
              <p className="text-lg">Análisis con IA</p>
              <p className="text-sm mt-2 text-center">
                Selecciona un producto y analízalo<br />
                para ver los resultados aquí
              </p>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatalogTesting;